/*
 * Homepage motion: a slow parallax drift on the explainer images.
 *
 * Dawn already reveals sections on scroll (settings.animations_reveal_on_scroll)
 * with a stagger per card, so this deliberately does not add another fade — it
 * adds the one thing the theme has no equivalent for: depth. The images sit
 * slightly behind the page and drift as it moves.
 *
 * Runs only on wide viewports with reduced motion off, and only while a card is
 * actually on screen. Transforms are applied inside the media wrapper, which
 * Dawn already clips, so the scaled image never overflows its frame.
 */
(() => {
  // The whole framed image drifts as a unit. An earlier version moved the
  // image INSIDE its frame and scaled it to cover the travel, which cropped
  // the explainer icons — never scale artwork to buy parallax headroom.
  const SELECTOR = '.multicolumn-card__image-wrapper';
  const DRIFT = 14; // px of travel at the extremes of the viewport

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wide = window.matchMedia('(min-width: 750px)');

  let images = [];
  let active = false;
  let ticking = false;

  const paint = () => {
    ticking = false;
    const viewport = window.innerHeight;

    images.forEach((img) => {
      const rect = img.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewport) return;

      // -1 at the bottom of the viewport, 0 centred, 1 at the top.
      const offset = (rect.top + rect.height / 2 - viewport / 2) / viewport;
      img.style.transform = `translate3d(0, ${(-offset * DRIFT).toFixed(2)}px, 0)`;
    });
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(paint);
  };

  const enable = () => {
    if (active) return;
    images = Array.from(document.querySelectorAll(SELECTOR));
    if (!images.length) return;
    active = true;
    images.forEach((img) => {
      img.style.willChange = 'transform';
      img.style.transition = 'transform 120ms linear';
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    paint();
  };

  const disable = () => {
    if (!active) return;
    active = false;
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
    images.forEach((img) => {
      img.style.transform = '';
      img.style.willChange = '';
      img.style.transition = '';
    });
  };

  const sync = () => (wide.matches && !reduced.matches ? enable() : disable());

  /*
   * Product cards: reveal on entry AND hide again on exit, so the entrance
   * replays every time the row is scrolled back to. Dawn's own reveal fires
   * once and stays. The hidden state lives behind .lff-reveal, added here, so
   * if this never runs the cards render normally.
   */
  const setupProductReveal = () => {
    if (reduced.matches || !('IntersectionObserver' in window)) return;

    const cards = Array.from(document.querySelectorAll('.product-grid .grid__item'));
    if (!cards.length) return;

    cards.forEach((card) => card.classList.add('lff-reveal'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-in', entry.isIntersecting);
        });
      },
      // A little inset at the bottom so a card is not called "in" while it is
      // still only a sliver above the fold.
      { threshold: 0, rootMargin: '0px 0px -12% 0px' }
    );

    cards.forEach((card) => observer.observe(card));
  };

  /*
   * The banded sections open vertically as they come into view. Progress runs
   * from "top edge at the bottom of the viewport" to "top edge a third of the
   * way up", and it is continuous, so scrolling back up closes them again.
   */
  const setupBandExpand = () => {
    if (reduced.matches) return;

    const bands = Array.from(
      document.querySelectorAll(
        '.shopify-section[id$="__multicolumn_WfA8Hp"],' +
          '.shopify-section[id$="__rich_text_TFnYXA"],' +
          '.shopify-section[id$="__rich_text_64kUnj"],' +
          '.shopify-section[id$="__rich_text_M7Exfw"]'
      )
    );
    if (!bands.length) return;

    const MAX_INSET = 12; // percent held back at each edge before it opens
    let bandTicking = false;
    const clamp01 = (n) => Math.min(1, Math.max(0, n));

    const paintBands = () => {
      bandTicking = false;

      // Phones get no widening at all. The effect waits for a band's BOTTOM
      // edge to reach the screen, and below 750px Dawn stacks the multicolumn
      // columns, so that band grows far taller than the viewport: its bottom
      // stays off-screen the whole time its top is being read, it sits at the
      // full inset throughout, and 12% of that height clipped the first icon
      // and pushed the arch down into a white gap. Removing the class gives the
      // no-JavaScript rendering — the band at its true size, which is known to
      // be right. Decided here rather than once at setup, so rotating a tablet
      // across the breakpoint switches cleanly in both directions.
      if (!wide.matches) {
        bands.forEach((band) => {
          band.classList.remove('band-expand');
          band.style.removeProperty('--band-inset-top');
          band.style.removeProperty('--band-inset-bottom');
        });
        return;
      }

      const viewport = window.innerHeight;

      // Every band is measured, including ones off screen. Skipping those
      // would leave them at the CSS default (fully open) until their first
      // paint, so they would snap shut the moment they scrolled into view.
      bands.forEach((band) => {
        const rect = band.getBoundingClientRect();

        // Both edges share one trigger, and it does not start until the band's
        // own bottom edge — and therefore the lower arch — is on screen.
        const progress = clamp01((viewport - rect.bottom) / (viewport * 0.45));
        const inset = ((1 - progress) * MAX_INSET).toFixed(2) + '%';

        band.classList.add('band-expand');
        band.style.setProperty('--band-inset-top', inset);
        band.style.setProperty('--band-inset-bottom', inset);
      });
    };

    const onBandScroll = () => {
      if (bandTicking) return;
      bandTicking = true;
      requestAnimationFrame(paintBands);
    };

    window.addEventListener('scroll', onBandScroll, { passive: true });
    window.addEventListener('resize', onBandScroll);
    paintBands();
  };

  /*
   * The first product row pins while downward scroll feeds it sideways.
   * The section is given extra height (one vertical px per horizontal px of
   * hidden products), its content turns sticky, and scroll progress through
   * that extra height maps straight onto the slider's scrollLeft. Releasing
   * the pin is nothing special — the sticky simply runs out of parent.
   *
   * Desktop-only (>= 990px, where Dawn's desktop slider exists): redirecting
   * vertical scroll into horizontal movement is hostile on touch, so below
   * that the row stays Dawn's native swipe slider.
   */
  const setupHorizontalPins = () => {
    const desktop = window.matchMedia('(min-width: 990px)');
    const sections = Array.from(
      document.querySelectorAll('.shopify-section[id*="__featured_collection_"]')
    );
    if (!sections.length) return;

    // Each product row gets its own pin with its own geometry; only the
    // enable/disable triggers are shared.
    const makePin = (sec) => {
      const sticky = sec.querySelector(':scope > .isolate') || sec.firstElementChild;
      const ul = sec.querySelector('ul.product-grid');
      if (!sticky || !ul) return null;

      let active = false;
      let hTicking = false;
      let overflow = 0;
      let pinTop = 0;

      const paint = () => {
        hTicking = false;
        if (!active) return;
        const travel = sec.offsetHeight - sticky.offsetHeight;
        if (travel <= 0) return;
        const progress = Math.min(
          1,
          Math.max(0, (pinTop - sec.getBoundingClientRect().top) / travel)
        );
        ul.scrollLeft = progress * overflow;
      };

      const onHScroll = () => {
        if (hTicking) return;
        hTicking = true;
        requestAnimationFrame(paint);
      };

      const measure = () => {
        // Measure the honest overflow with the pin released.
        sec.style.height = '';
        sec.classList.remove('lff-hpin');
        overflow = Math.max(0, ul.scrollWidth - ul.clientWidth);
        if (overflow < 80) return false; // nothing meaningful to feed
        sec.classList.add('lff-hpin');
        sec.style.height = sticky.offsetHeight + overflow + 'px';
        pinTop = Math.max(12, (window.innerHeight - sticky.offsetHeight) / 2);
        sec.style.setProperty('--hpin-top', pinTop + 'px');
        return true;
      };

      const enable = () => {
        if (active) return;
        if (!measure()) return;
        active = true;
        window.addEventListener('scroll', onHScroll, { passive: true });
        paint();
      };

      const disable = () => {
        if (!active) return;
        active = false;
        window.removeEventListener('scroll', onHScroll);
        sec.classList.remove('lff-hpin');
        sec.style.height = '';
        sec.style.removeProperty('--hpin-top');
      };

      const sync = () =>
        desktop.matches && !reduced.matches ? enable() : disable();

      const resize = () => {
        // Re-check the gate, not just the geometry: a resize can cross the
        // 990px boundary without this browser firing the matchMedia change
        // event first, and a re-measure alone would then keep the pin alive
        // with mobile geometry — arrows hidden and manual swiping blocked.
        if (!desktop.matches || reduced.matches) {
          disable();
          return;
        }
        if (!active) {
          enable();
          return;
        }
        if (!measure()) disable();
        else paint();
      };

      return { sync, resize };
    };

    const pins = sections.map(makePin).filter(Boolean);
    if (!pins.length) return;

    const syncAll = () => pins.forEach((p) => p.sync());
    const resizeAll = () => pins.forEach((p) => p.resize());

    syncAll();
    desktop.addEventListener('change', syncAll);
    reduced.addEventListener('change', syncAll);
    window.addEventListener('resize', resizeAll);
    // Card images load lazily and change the rows' widths; re-measure once
    // everything that affects scrollWidth has actually arrived.
    window.addEventListener('load', resizeAll, { once: true });
  };

  const start = () => {
    sync();
    setupProductReveal();
    setupBandExpand();
    setupHorizontalPins();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  wide.addEventListener('change', sync);
  reduced.addEventListener('change', sync);
})();
